/**
 * Pricing catalog — Structura Food Market.
 * Tiers comerciales definitivos (ARS-only SaaS pricing).
 */

export type SmPlanId = "starter" | "pro" | "business" | "enterprise" | "free_trial";

export interface SmPlanDef {
  id: SmPlanId;
  name: string;
  tagline: string;
  /** Legacy; not used for charges or public UI. Prefer null for paid plans. */
  priceUsd: number | null;
  /** Monthly price in whole pesos (ARS). Source of truth for billing/UI. */
  priceArs: number | null;
  annualDiscount?: boolean;
  highlighted?: boolean;
  limits: {
    products: number | null;
    ordersPerMonth: number | null;
    locations: number | null;
    storefront: boolean;
    portalB2b: boolean;
  };
  /** Core capabilities incluidas en marketing (BYO vía feature-catalog planTier) */
  featureIds: string[];
}

/** Core Structura — incluidas por plan (sin providers BYO). */
const FM_STARTER_CORE = [
  "catalog_public",
  "orders_online",
  "pesaje_module",
  "stock_management",
  "widget_embed",
  "payment_qr",
] as const;

const FM_PRO_CORE_ADD = [
  "multi_location",
  "variants_addons",
  "customer_accounts",
  "loyalty_points",
  "gift_cards",
  "custom_branding",
  "custom_domain",
  "analytics_advanced",
  "integrations_merchant",
  "feature_presets",
  "exports_csv",
  "invoicing_tusfacturas",
  "invoicing_afip_direct",
] as const;

const FM_BUSINESS_CORE_ADD = [
  "produccion_b2b",
  "institutional_orders",
  "payment_marketplace_ready",
  "shipping_multi_carrier",
  "payment_pos_mode",
] as const;

/** Hardware Integration Platform — drivers custom, multi-dispositivo, monitoreo. */
const FM_ENTERPRISE_CORE_ADD = [
  "hardware_custom_drivers",
  "hardware_multi_device",
  "hardware_monitoring",
] as const;

export const SM_PLANS: SmPlanDef[] = [
  {
    id: "starter",
    name: "Empezá a vender",
    tagline: "Tienda online, pedidos, stock y peso — 1 sucursal",
    priceUsd: null,
    priceArs: 34900,
    limits: {
      products: 200,
      ordersPerMonth: 100,
      locations: 1,
      storefront: true,
      portalB2b: false,
    },
    featureIds: [...FM_STARTER_CORE],
  },
  {
    id: "pro",
    name: "Operación diaria",
    tagline: "Branding, clientes, loyalty y múltiples sucursales",
    priceUsd: null,
    priceArs: 85900,
    highlighted: true,
    limits: {
      products: 2000,
      ordersPerMonth: 500,
      locations: 5,
      storefront: true,
      portalB2b: false,
    },
    featureIds: [...FM_STARTER_CORE, ...FM_PRO_CORE_ADD],
  },
  {
    id: "business",
    name: "Escalá operación",
    tagline: "Portal institucional, workflows y fair-use ilimitado",
    priceUsd: null,
    priceArs: 172900,
    limits: {
      products: null,
      ordersPerMonth: null,
      locations: null,
      storefront: true,
      portalB2b: true,
    },
    featureIds: [...FM_STARTER_CORE, ...FM_PRO_CORE_ADD, ...FM_BUSINESS_CORE_ADD],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Hardware a medida, multi-dispositivo y monitoreo",
    priceUsd: null,
    priceArs: 259900,
    limits: {
      products: null,
      ordersPerMonth: null,
      locations: null,
      storefront: true,
      portalB2b: true,
    },
    featureIds: [...FM_STARTER_CORE, ...FM_PRO_CORE_ADD, ...FM_BUSINESS_CORE_ADD, ...FM_ENTERPRISE_CORE_ADD],
  },
  {
    id: "free_trial",
    name: "Trial gratuito",
    tagline: "14 días con acceso Pro completo",
    priceUsd: 0,
    priceArs: 0,
    limits: {
      products: 2000,
      ordersPerMonth: 500,
      locations: 5,
      storefront: true,
      portalB2b: false,
    },
    featureIds: [...FM_STARTER_CORE, ...FM_PRO_CORE_ADD],
  },
];

export function getPlanById(id: SmPlanId | string | null | undefined): SmPlanDef | undefined {
  return SM_PLANS.find((p) => p.id === id);
}

/** Monthly ARS pesos (whole). Source of truth for SaaS plan charges. */
export function planPriceArs(plan: { priceArs?: number | null } | null | undefined): number {
  const n = Number(plan?.priceArs ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Monthly ARS amount in cents (pesos × 100). */
export function planMonthlyCents(plan: { priceArs?: number | null } | null | undefined): number {
  return Math.round(planPriceArs(plan) * 100);
}

/** Compact ARS for plan cards/headers (`$34.900`, no space after `$`). */
export function formatPlanArs(amount: number): string {
  const n = Number.isFinite(amount) ? Math.round(amount) : 0;
  const digits = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(n);
  return `$${digits}`;
}

/** Plan amount + period without spaces around `/` (`$1.659.840/año`). */
export function formatPlanArsPeriod(amount: number, period: "mes" | "año"): string {
  return `${formatPlanArs(amount)}/${period}`;
}

export const MARKETING_PLANS = SM_PLANS.filter((p) => p.id !== "free_trial");

export const HIGHLIGHTED_PLAN = SM_PLANS.find((p) => p.highlighted);
